import { FormManager } from 'react-form-state-manager';
import { Trans, useTranslation } from 'react-i18next';

import { ErrorBag } from '../../common/helpers';
import UI from '../../common/UI';
import useProject from '../useProject';

interface Product {
  id: string;
  title: string;
}

interface ProductForm {
  products: { id: string }[];
}

type ProductSelectionFormProps<TProduct extends Product> = {
  form: FormManager<ProductForm>;
  products: TProduct[];
  errors: ErrorBag;
  event: { id: string };
  isDisabled?: (product: TProduct) => boolean,
};

function ProductSelectionForm<TProduct extends Product>({
  form, products, errors, event, isDisabled,
}: ProductSelectionFormProps<TProduct>) {
  const { t } = useTranslation();
  const project = useProject();

  const allAvailableProducts = typeof isDisabled === 'undefined'
    ? products
    : products.filter((product) => !isDisabled(product));

  const selectAllProducts = () => {
    form.set('products', allAvailableProducts.map(({ id }) => ({ id })));
  };

  const deselectAllProducts = () => {
    form.set('products', []);
  };

  return (
    <UI.InputGroup sc={{ valid: !errors.products, touched: form.touched.products }}>
      <UI.InputLabel>
        {t('products')}
        <UI.Span sc={{ muted: true }}> • </UI.Span>
        <UI.A
          onClick={() => selectAllProducts()}
          sc={{ disabled: form.values.products.length === allAvailableProducts.length }}
        >
          {t('select_all')}
        </UI.A>
        <UI.Span sc={{ muted: true }}> • </UI.Span>
        <UI.A
          onClick={() => deselectAllProducts()}
          sc={{ disabled: form.values.products.length === 0 }}
        >
          {t('deselect_all')}
        </UI.A>
      </UI.InputLabel>
      {products.map((product) => (
        <UI.Checkbox
          {...form.checklist('products', { id: product.id }, { key: 'id' })}
          key={product.id}
          disabled={typeof isDisabled === 'undefined' ? false : isDisabled(product)}
        >
          {product.title}
        </UI.Checkbox>
      ))}
      {products.length === 0 && (
        <UI.Div sc={{ muted: true, py: 1 }}>
          <Trans i18nKey="no_products_created">
            <UI.Link to={`/${project.id}/events/${event.id}/products/add`} />
          </Trans>
        </UI.Div>
      )}
      <UI.ErrorMessages attribute={t('products')} errors={errors.products} />
    </UI.InputGroup>
  );
}

export default ProductSelectionForm;
